// ============================================================
// COMPLIANCE — Runtime helpers that enforce ASP guardrails
// ============================================================
// See COMPLIANCE.md for the full list of contractual rules.
// These helpers prevent us from accidentally rendering UI that
// would violate the Peplink ASP Term Sheet.

import { PROHIBITED_USE_CASES } from '../config/brand.js';

/**
 * Guardrail #2 (§ 4.2): determines whether a registering user's
 * declared use-case is prohibited. Called by the signup gate.
 * Returns the matching prohibited-case record, or null if OK.
 */
export function checkProhibitedUseCase(useCaseId) {
  if (!useCaseId) return null;
  return PROHIBITED_USE_CASES.find((c) => c.id === useCaseId) || null;
}

/**
 * Guardrail #5 (§ 11.3, 11.4, 11.5): trademark/logo check on
 * dynamically-loaded asset URLs. We never want to render an
 * image that is a Starlink/SpaceX trademark. Run this on any
 * URL we receive from an upstream API before rendering.
 */
const FORBIDDEN_ASSET_PATTERNS = [
  /starlink[-_]?logo/i,
  /spacex/i,
  /falcon[-_]rocket/i,
  /dragon[-_]capsule/i,
];

export function isForbiddenAssetUrl(url) {
  if (!url || typeof url !== 'string') return false;
  return FORBIDDEN_ASSET_PATTERNS.some((pattern) => pattern.test(url));
}

/**
 * Guardrail #6 (§ 11.2): branding copy linter. Ensures any
 * dynamically-generated copy we ship to the UI doesn't use
 * forbidden phrases.
 */
const FORBIDDEN_PHRASES = [
  'Authorized Starlink Reseller',
  'Authorized Starlink Retailer',
  'Authorized Channel Provider',
  'Starlink Partner',
  'Starlink Partnership',
  'partnership with Starlink',
  'collaboration with Starlink',
  'collaboration with SpaceX',
  'in partnership with SpaceX',
  // 🚨 2026-09-10 — Netcom left the Peplink & Starlink ASP Program. The
  // designation that used to be REQUIRED here is now forbidden in every
  // spelling. Do not add a reseller claim in its place: the SpaceX
  // direct-reseller agreement is unsigned.
  'Authorized Peplink and Starlink Solution Provider',
  'Authorized Peplink & Starlink Solution Provider',
  'Authorized Peplink &amp; Starlink Solution Provider',
  'Starlink Solution Provider',
  'Starlink Authorized Reseller',
];

export function hasForbiddenPhrase(text) {
  if (!text || typeof text !== 'string') return false;
  const lower = text.toLowerCase();
  return FORBIDDEN_PHRASES.some((phrase) => lower.includes(phrase.toLowerCase()));
}

/**
 * Guardrail #7 (§ 3.2(a)): pricing sanitizer. Ensures we never
 * leak internal cost / wholesale fields to the client. Called on
 * any pricing object returned by an upstream API.
 *
 * Allowed fields: msrp, listPrice, billingCycle, planName.
 * Blocked fields: cost, wholesale, margin, internalPrice.
 */
// Customer-facing display fields are allowed. The point of this allowlist
// is to BLOCK internal-only fields like `cost`, `wholesale`, `margin`,
// `internalPrice`, `supplierCost`, etc. — never to strip prices we're
// supposed to show the customer.
const ALLOWED_PRICING_FIELDS = new Set([
  'msrp', 'listPrice', 'amount', 'price', 'currency', 'billingCycle',
  'planName', 'planId', 'productReferenceId', 'nickname', 'serviceLineNumber',
  'planChargeUsd', 'terminalAccessChargeUsd', 'topUpsUsd', 'subtotalUsd',
  'estimatedTotalUsd', 'totalUsd', 'status', 'cycleStart', 'startedAt', 'endsAt',
]);

// Fields that MUST be stripped from any API response before reaching the client.
// 2026-05-29: extended with KORE ConnectivityPro wholesale fields so the SIM
// Cards page never leaks Netcom's negotiated pricing back to the customer.
// KORE field names per Developer Portal docs; covers both common camelCase
// + snake_case forms because Lambda may serialize either way.
const BLOCKED_PRICING_FIELDS = new Set([
  // Starlink wholesale
  'cost', 'wholesale', 'wholesalePrice', 'margin', 'marginPercent',
  'internalPrice', 'supplierCost', 'supplierName', 'commission',
  // KORE ConnectivityPro wholesale (snake_case in some payloads)
  'wholesale_rate', 'wholesaleRate', 'wholesale_cost', 'wholesaleCost',
  'tier_discount', 'tierDiscount', 'tier_discount_pct',
  'cost_center_internal', 'costCenterInternal',
  'net_rate', 'netRate', 'partner_rate', 'partnerRate',
  'mvno_cost', 'mvnoCost', 'carrier_cost', 'carrierCost',
  // KORE raw upstream payload — can carry white-label tokens ("KORE",
  // "OmniSIM", "Reach") that must never reach the client (KORE stays
  // internal). The Lambda should strip it too; this is defense-in-depth.
  '_rawProductOffer',
  // Any field that wraps an `internal_*` / `_internal` suffix is suspicious;
  // we leave it to the developer to add specific names. Pattern-matching
  // would be safer but the explicit list is auditable.
]);

/**
 * Strip BLOCKED fields, keep everything else. (Switched from allowlist
 * to blocklist 2026-05-27 — allowlist was too restrictive and dropped
 * legitimate display fields like planChargeUsd. Blocklist is the right
 * model: we KNOW what's confidential, everything else is a display
 * field that can pass through.)
 */
export function sanitizePricing(pricingObject) {
  if (!pricingObject || typeof pricingObject !== 'object') return pricingObject;
  const safe = {};
  for (const [key, value] of Object.entries(pricingObject)) {
    if (BLOCKED_PRICING_FIELDS.has(key)) continue;
    // Pattern guard: drop any raw/internal upstream field (e.g. _rawProductOffer,
    // _internal*, *_internal) so a future backend leak degrades safely.
    if (/^_raw|_internal$|^internal_/i.test(key)) continue;
    safe[key] = value;
  }
  return safe;
}

/**
 * Strict allowlist version — used when you only want a tiny pre-approved
 * set of fields. Kept around for sensitive contexts.
 */
export function sanitizePricingStrict(pricingObject) {
  if (!pricingObject || typeof pricingObject !== 'object') return pricingObject;
  const safe = {};
  for (const [key, value] of Object.entries(pricingObject)) {
    if (ALLOWED_PRICING_FIELDS.has(key)) safe[key] = value;
  }
  return safe;
}

/**
 * Guardrail #11 (§ 10.2): URL safety check. Throws if any code
 * path tries to fetch from the Peplink ASP Portal — that's a
 * contract violation. Wrap the global fetch in this helper.
 */
const FORBIDDEN_FETCH_HOSTS = [
  'asp.peplink.com',
  'partners.peplink.com',
];

export function assertSafeFetchUrl(url) {
  try {
    const parsed = new URL(url, 'http://localhost');
    if (FORBIDDEN_FETCH_HOSTS.includes(parsed.hostname)) {
      throw new Error(
        `[COMPLIANCE VIOLATION] Fetch to ${parsed.hostname} is forbidden by Term Sheet § 10.2. ` +
        `The Peplink ASP Portal must not be scraped or automated against.`
      );
    }
  } catch (e) {
    if (e.message.startsWith('[COMPLIANCE VIOLATION]')) throw e;
    // Otherwise URL parsing failed — likely a relative URL, OK.
  }
}

/**
 * Wrap window.fetch so all outgoing requests pass through the
 * URL safety check. Call this once at app boot.
 */
export function installFetchGuardrail() {
  if (typeof window === 'undefined') return;
  if (window.__netcom_fetch_guarded) return;
  const originalFetch = window.fetch;
  window.fetch = function guardedFetch(input, init) {
    const url = typeof input === 'string' ? input : input?.url;
    if (url) assertSafeFetchUrl(url);
    return originalFetch.call(this, input, init);
  };
  window.__netcom_fetch_guarded = true;
}
